import { isPlatformBrowser } from '@angular/common';
import type { Renderer2 } from '@angular/core';
import {
	DOCUMENT,
	inject,
	Injectable,
	InjectionToken,
	PLATFORM_ID,
	RendererFactory2,
} from '@angular/core';

declare global {
	interface Window {
		dataLayer: unknown[];
		hasLoadedGtmTags?: boolean;
	}
}

export const GTM_CODE = new InjectionToken<string[]>('gtm-code');

/**
 * Opt-in flag for pushing a Google Consent Mode v2 default (all tracking
 * storage denied) to the dataLayer before the GTM container loads. Off by
 * default so existing apps are unaffected; the guest app enables it so that
 * analytics/marketing tags stay dormant until the user consents (and, on iOS,
 * until App Tracking Transparency is authorized).
 */
export const GTM_CONSENT_DEFAULT = new InjectionToken<boolean>(
	'gtm-consent-default',
);

@Injectable({
	providedIn: 'root',
})
export class GoogleTagManagerService {
	document = inject<Document>(DOCUMENT);
	platformId = inject(PLATFORM_ID);
	readonly rendererFactory = inject(RendererFactory2);
	readonly gtmCode = inject(GTM_CODE);
	readonly consentDefault =
		inject(GTM_CONSENT_DEFAULT, { optional: true }) ?? false;

	private readonly renderer: Renderer2;
	private readonly window: Window | null;

	constructor() {
		this.renderer = this.rendererFactory.createRenderer(null, null);
		this.window = this.document.defaultView;
	}

	init() {
		if (!this.gtmCode) {
			return;
		}
		if (
			isPlatformBrowser(this.platformId) &&
			this.window?.hasLoadedGtmTags
		) {
			return;
		}

		if (this.consentDefault) {
			this.setDefaultConsent();
		}

		this.createGoogleTagManagerScript();
		this.createGoogleTagManagerNoScript();

		//NOTE: To transfer the hasLoadedGtmTags from ssr to client
		const verificationScriptTag = this.renderer.createElement('script');
		verificationScriptTag.innerHTML = 'window.hasLoadedGtmTags = true;';
		this.renderer.appendChild(this.document.head, verificationScriptTag);
	}

	/**
	 * Push the Google Consent Mode v2 default to the dataLayer. Must run
	 * *before* the container script so consent-aware analytics/advertising
	 * tags stay dormant until an explicit consent update grants them. This is
	 * what prevents tracking from firing before the user consents — and, on
	 * iOS, before App Tracking Transparency authorization.
	 *
	 * Note: for this to gate anything, the GTM container's tags must be
	 * configured for Consent Mode and a matching `consent: update` must be
	 * pushed on acceptance. It is an inert no-op for containers that gate
	 * purely on custom trigger events.
	 */
	private setDefaultConsent(): void {
		if (!isPlatformBrowser(this.platformId) || !this.window) {
			return;
		}

		// Ensure the queue exists before the container script creates it.
		this.window.dataLayer = this.window.dataLayer ?? [];

		// Consent Mode uses the positional `gtag(...)` form, which is
		// `dataLayer.push(arguments)` — i.e. an array, not a keyed object.
		this.window.dataLayer.push([
			'consent',
			'default',
			{
				ad_storage: 'denied',
				ad_user_data: 'denied',
				ad_personalization: 'denied',
				analytics_storage: 'denied',
				functionality_storage: 'granted',
				security_storage: 'granted',
				wait_for_update: 500,
			},
		]);
	}

	createGoogleTagManagerScript() {
		if (!this.gtmCode) {
			return;
		}
		const head: HTMLElement = this.document.head;
		const scriptElement = this.renderer.createElement('script');

		scriptElement.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
		new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
		j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
		'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
		})(window,document,'script','dataLayer','${this.gtmCode}');`;

		this.renderer.insertBefore(head, scriptElement, head.firstChild);
	}

	createGoogleTagManagerNoScript() {
		if (!this.gtmCode) {
			return;
		}
		const body: HTMLElement = this.document.body;
		const noScriptElement = this.renderer.createElement('noscript');
		noScriptElement.innerText = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.gtmCode}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
		this.renderer.insertBefore(body, noScriptElement, body.firstChild);
	}

	public push<T>(event: T): void {
		if (!this.gtmCode || !isPlatformBrowser(this.platformId)) {
			return;
		}

		this.window?.dataLayer?.push(event);
	}
}
